import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Zap, BookOpen, Clock, AlertTriangle, CheckCircle2, ExternalLink, RefreshCw } from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';
import FooterBackground from '../components/FooterBackground';
import NewsletterSignup from '../components/NewsletterSignup';
import SEOHead from '../components/SEOHead';
import { guides, guideList } from '../data/guides';
import { blogPostList } from '../data/blogPosts';

const IMPACT_TONE = {
  High: { dot: 'bg-rose-400', text: 'text-rose-400' },
  Medium: { dot: 'bg-amber-400', text: 'text-amber-400' },
  Low: { dot: 'bg-slate-400', text: 'text-slate-600' },
};

// v34.99 (Feb 14 2026) — Last-update date stamped on every guide. Updated
// when we materially edit a guide's patterns/intro. Google rewards freshness
// signals so we surface this in the page header + JSON-LD `dateModified`.
const LAST_UPDATED = 'Feb 14, 2026';

export default function GuideDetail() {
  const { slug } = useParams();
  const guide = guides[slug];

  // v34.99 — Sticky bottom-right "run free audit" pill appears after the
  // reader has scrolled ~40% of the page. Disappears when the static CTA
  // is in view to avoid duplicate affordances stacked on top of each other.
  const [showStickyCta, setShowStickyCta] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const scrolled = (window.scrollY + window.innerHeight) / document.body.scrollHeight;
      const ctaInView = !!document.querySelector('[data-testid="guide-cta"]')?.getBoundingClientRect().top
        && document.querySelector('[data-testid="guide-cta"]')?.getBoundingClientRect().top < window.innerHeight;
      setShowStickyCta(scrolled > 0.4 && !ctaInView);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!guide) return <Navigate to="/guides" replace />;

  const otherGuides = guideList.filter((g) => g.slug !== slug).slice(0, 4);

  // v34.99 — Pull the 3 most recent blog posts that mention this cloud by
  // name (case-insensitive) in their slug, title, or keywords. Powers the
  // "Related from the Blog" strip below — cross-pollinates SEO between
  // guides + blog and gives readers a deeper rabbit hole.
  const needle = (guide.name || guide.slug).toLowerCase();
  const relatedPosts = (blogPostList || [])
    .filter((p) => {
      const hay = `${p.slug || ''} ${p.title || ''} ${p.keywords || ''}`.toLowerCase();
      return hay.includes(needle);
    })
    .slice(0, 3);

  // JSON-LD HowTo schema for organic search rich results
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: guide.title,
    description: guide.metaDescription,
    step: guide.patterns.map((p, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: p.title,
      text: p.desc,
    })),
  };

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid={`guide-${guide.slug}`}>
      <SEOHead
        title={guide.seoTitle}
        description={guide.metaDescription}
        canonical={`https://cartieai.com/guides/${guide.slug}`}
        keywords={guide.keywords}
        schema={jsonLd}
      />

      <PublicNavbar />

      <section className="relative px-4 pt-24 pb-10 overflow-hidden">
        <FooterBackground />
        <div className="relative max-w-4xl mx-auto">
          <Link
            to="/guides"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-white transition-colors mb-6"
            data-testid="guide-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>All guides</span>
          </Link>

          <div className="text-4xl mb-4">{guide.icon}</div>

          <div className="text-xs font-bold tracking-widest text-slate-400 uppercase mb-3">
            {guide.longName} · FinOps Field Guide
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
            {guide.title}
          </h1>

          {/* v34.99 — Freshness signal. Surfaces last-update date next to the
              CARTIEAI byline so Google sees a `dateModified` next to the
              JSON-LD HowTo and rewards the page in rankings. */}
          <div className="flex items-center gap-3 text-xs text-slate-400 mb-5" data-testid="guide-freshness">
            <span className="inline-flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Last updated <span className="text-slate-300 font-medium">{LAST_UPDATED}</span></span>
            </span>
            <span className="text-slate-700">•</span>
            <span>Maintained by the CARTIEAI team</span>
          </div>

          <p className="text-lg text-slate-400 max-w-3xl mb-7 leading-relaxed">{guide.intro}</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2">
            {guide.quickStats.map((s) => (
              <div
                key={s.label}
                className="rounded-xl bg-white/5 border border-white/10 px-4 py-3"
              >
                <div className={`text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${guide.accent}`}>
                  {s.value}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 mt-4">
            The {guide.patterns.length} patterns
          </h2>

          <div className="space-y-4">
            {guide.patterns.map((p, i) => {
              const tone = IMPACT_TONE[p.impact] || IMPACT_TONE.Medium;
              return (
                <div
                  key={p.title}
                  className="rounded-xl bg-white/5 border border-white/10 p-5 hover:border-white/20 transition-colors"
                  data-testid={`guide-pattern-${i + 1}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-sm font-bold text-slate-700">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <h3 className="text-lg font-semibold">{p.title}</h3>
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                          <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                          <span className={tone.text}>{p.impact} impact</span>
                        </span>
                        <span className="text-xs text-slate-400">{p.effort} effort</span>
                        <span className={`text-xs font-bold bg-clip-text text-transparent bg-gradient-to-r ${guide.accent}`}>
                          {p.savings}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-16">
        <div
          className={`max-w-4xl mx-auto rounded-2xl p-8 sm:p-10 bg-gradient-to-br ${guide.accent} relative overflow-hidden`}
          data-testid="guide-cta"
        >
          <div className="absolute inset-0 bg-slate-950/40" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-white/80 uppercase mb-3">
              <Zap className="w-3.5 h-3.5" />
              <span>Free audit</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mb-3">{guide.cta.title}</h3>
            <p className="text-white/85 text-base mb-6 max-w-2xl">{guide.cta.desc}</p>
            <Link
              to={guide.cta.href}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-slate-900/60 text-slate-900 font-semibold hover:bg-white/90 transition-colors"
              data-testid="guide-cta-button"
            >
              <span>{guide.cta.label}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          <NewsletterSignup variant="dark" source={`guide_${guide.slug}`} />
        </div>
      </section>

      {/* v34.99 — Related blog posts. Cross-pollinates SEO between
          /guides and /blog. Only renders if we found 1+ posts that
          mention this cloud by name. */}
      {relatedPosts.length > 0 && (
        <section className="px-4 pb-14" data-testid="guide-related-posts">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-300 inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-emerald-400" />
                <span>Latest from the blog on {guide.name}</span>
              </h3>
              <Link
                to="/blog"
                className="text-sm text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1"
                data-testid="guide-blog-all"
              >
                <span>All posts</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {relatedPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group rounded-xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 hover:border-emerald-500/30 transition-colors flex flex-col"
                  data-testid={`guide-related-post-${post.slug}`}
                >
                  <div className="text-[10px] font-bold tracking-widest text-emerald-300 uppercase mb-2">
                    {post.category || 'Blog'} · {post.readTime || '—'}
                  </div>
                  <h4 className="text-sm font-semibold text-white leading-snug group-hover:text-emerald-200 mb-2 line-clamp-3">
                    {post.title}
                  </h4>
                  <p className="text-xs text-slate-400 line-clamp-3 leading-relaxed flex-1">
                    {post.excerpt}
                  </p>
                  <div className="mt-3 text-xs text-emerald-300 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    <span>Read</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other guides */}
      <section className="px-4 pb-20">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold mb-4 text-slate-300">More platform guides</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {otherGuides.map((g) => (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                className="rounded-lg bg-white/5 border border-white/10 px-4 py-3 hover:bg-white/10 transition-colors flex items-center gap-3"
                data-testid={`guide-related-${g.slug}`}
              >
                <span className="text-xl">{g.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{g.longName}</div>
                  <div className="text-xs text-slate-400 truncate">{g.patterns.length} patterns</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>
      {/* v34.99 — Sticky "Run free audit" CTA. Appears after 40% scroll,
          hides when the static CTA card is in view, vanishes off-screen. */}
      {showStickyCta && (
        <Link
          to={guide.cta.href}
          data-testid="guide-sticky-cta"
          className={`fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm shadow-2xl shadow-emerald-500/30 ring-1 ring-white/20 bg-gradient-to-br ${guide.accent} text-white hover:scale-[1.03] transition-transform`}
        >
          <Zap className="w-4 h-4 drop-shadow-sm" />
          <span className="hidden sm:inline">Run a free {guide.name} audit</span>
          <span className="sm:hidden">Free audit</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}
